import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { ApiError, peek } from '../api.js';
import { ddm, ddmSv, telHref } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import { Icon, RoleIcon } from '../shell.js';
import { Avatar, Callout, LoadError, Loading, toast } from '../ui.js';

/**
 * "Inför inspelningen" (owner 2026-11-01, thread "Förenkla varje sida per roll"). Only the camera is on set (owner
 * 2026-10-30), so the day before, the camera crew gets the shoot on one page: when and where (Maps, calendar), who to
 * ring, what is filmed, the brief in short, the actors and their releases, and what earlier shoots at the client taught.
 * "Jag har läst in mig" shows the producer the camera is ready. The banner on My work leads here, as do the notice the
 * morning before and the reminder an hour before.
 */

interface PrepPerson {
  id: string;
  name: string;
  phone: string | null;
}
export interface Sheet {
  shoot: { id: string; date: string; start: string; end: string; location: string; plan: string; status: 'booked' | 'cancelled'; cancelReason: string | null };
  project: { id: string; name: string };
  client: string | null;
  onSite: (PrepPerson & { title: string | null }) | null;
  producers: PrepPerson[];
  coordinator: PrepPerson | null;
  crew: (PrepPerson & { role: string })[];
  videos: { id: string; name: string }[];
  brief: { key: string; label: string; text: string }[];
  actors: { name: string; role: string | null; release: 'signed' | 'sent' | 'missing' }[];
  tips: { date: string; project: string; by: string | null; text: string }[];
  readers: { id: string; name: string; readAt: string | null }[];
  me: { id: string; reader: boolean; readAt: string | null };
}
export interface PrepBanner {
  shootId: string;
  project: { id: string; name: string };
  client: string | null;
  date: string;
  start: string;
  end: string;
  location: string;
  today: boolean;
}

const T = texts(
  {
    title: 'Before the shoot',
    back: 'Shoots',
    cancelled: 'The shoot is cancelled',
    maps: 'Open in Maps',
    calendar: 'Add to calendar',
    call: 'Call',
    onSite: 'contact on site',
    producer: 'producer',
    coordinator: 'coordinator',
    role: { producer: 'producer', cd: 'CD', camera: 'camera', editor: 'editing', other: 'crew' } as Record<string, string>,
    noPhone: 'no phone',
    filming: 'What we film',
    wholeProject: 'The whole project',
    plan: 'Plan',
    brief: 'The brief in short',
    fullBrief: 'The whole brief',
    files: 'Files',
    actors: 'In the films',
    signed: 'Release signed',
    sent: 'Release sent, not signed',
    missing: 'No release',
    tips: 'Good to know',
    tipFrom: (date: string, project: string, by: string | null) => `After the shoot ${date} · ${project}${by ? ` · ${by}` : ''}`,
    noTips: 'Nothing from earlier shoots at this client yet.',
    rushes: 'After the shoot the rushes go into RUSHES in the project folder; the Hub reminds you.',
    readers: 'Read in',
    notYet: 'not yet',
    readIn: 'I have read in',
    readAt: (at: string) => `You read in ${at}`,
    banner: (when: string, client: string | null) => `Shoot ${when}${client ? ` · ${client}` : ''}`,
    tomorrow: 'tomorrow',
    today: 'today',
    bannerSub: 'Read in before you go: it takes a couple of minutes.',
    open: 'Read before the shoot'
  },
  {
    title: 'Inför inspelningen',
    back: 'Inspelningar',
    cancelled: 'Inspelningen är inställd',
    maps: 'Öppna i Kartor',
    calendar: 'Lägg i kalender',
    call: 'Ring',
    onSite: 'kontakt på plats',
    producer: 'producent',
    coordinator: 'koordinator',
    role: { producer: 'producent', cd: 'CD', camera: 'kamera', editor: 'redigering', other: 'crew' } as Record<string, string>,
    noPhone: 'inget nummer',
    filming: 'Det här filmar vi',
    wholeProject: 'Hela projektet',
    plan: 'Plan',
    brief: 'Briefen i korthet',
    fullBrief: 'Hela briefen',
    files: 'Filer',
    actors: 'Medverkande',
    signed: 'Avtal signerat',
    sent: 'Avtal skickat, inte signerat',
    missing: 'Avtal saknas',
    tips: 'Bra att veta',
    tipFrom: (date: string, project: string, by: string | null) => `Efter inspelningen ${date} · ${project}${by ? ` · ${by}` : ''}`,
    noTips: 'Inget från tidigare inspelningar hos kunden än.',
    rushes: 'Efter inspelningen laddas rushes upp i RUSHES i projektmappen; Hubben påminner dig.',
    readers: 'Har läst in sig',
    notYet: 'inte än',
    readIn: 'Jag har läst in mig',
    readAt: (at: string) => `Du läste in dig ${at}`,
    banner: (when: string, client: string | null) => `Inspelning ${when}${client ? ` · ${client}` : ''}`,
    tomorrow: 'i morgon',
    today: 'i dag',
    bannerSub: 'Läs in dig innan du åker, det tar ett par minuter.',
    open: 'Läs inför inspelningen'
  }
);

const day = (k: string, lang: Lang) => (lang === 'sv' ? ddmSv(k) : ddm(k));
const stamp = (iso: string, lang: Lang) =>
  new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', { timeZone: 'Europe/Stockholm', weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** Maps: the address as a search, which every phone opens in its own maps app. */
export const mapsHref = (place: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}`;

/** A Google Calendar event to save (the invite itself comes from coordinator@ when the Hub sends it). */
export function calendarHref(s: Sheet['shoot'], title: string): string {
  const t = (hm: string) => `${s.date.replaceAll('-', '')}T${hm.replace(':', '')}00`;
  const q = new URLSearchParams({ action: 'TEMPLATE', text: title, dates: `${t(s.start)}/${t(s.end)}`, ctz: 'Europe/Stockholm', location: s.location, details: s.plan });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}

function Person(p: { name: string; sub: string; phone: string | null; role?: string }) {
  const t = T[useLang()];
  return (
    <li class="prep-person">
      <Avatar name={p.name} size="sm" decorative />
      <span class="prep-who">
        <strong>{p.name}</strong>
        <span class="small muted">
          {p.role === 'camera' || p.role === 'editor' ? <RoleIcon role={p.role} label={p.sub} /> : null} {p.sub}
        </span>
      </span>
      {p.phone ? (
        <a class="btn btn-sm prep-call" href={telHref(p.phone)} aria-label={`${t.call} ${p.name}, ${p.phone}`}>
          <Icon name="phone" />
          <span>{t.call}</span>
        </a>
      ) : (
        <span class="small muted">{t.noPhone}</span>
      )}
    </li>
  );
}

/** /projects/:id/shoots/:shootId */
export function ShootPrep(p: { api: Api; projectId: string; shootId: string }) {
  const lang = useLang();
  const t = T[lang];
  const url = `/api/projects/${p.projectId}/shoots/${p.shootId}/prep`;
  const [v, setV] = useState<Sheet | null>(() => peek<Sheet>(p.api, url) ?? null);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    p.api.get<Sheet>(url).then(setV, setErr);
  }, [url]);
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading />;
  const s = v.shoot;
  const back = `/projects/${p.projectId}/shoots`;
  const read = async () => {
    setBusy(true);
    try {
      const r = await p.api.post<{ readAt: string; text: string }>(`${url}/read`);
      setV({ ...v, me: { ...v.me, readAt: r.readAt } });
      toast({ text: r.text });
      // the "Read in" list gets the new tick from the server
      p.api.get<Sheet>(url).then(setV, () => undefined);
    } catch (e) {
      toast({ text: e instanceof ApiError || e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  // the people to ring: the client's contact first (the camera meets them), then the producer, the coordinator, the crew
  const producerIds = new Set(v.producers.map((x) => x.id));
  // the reader does not need to ring themselves
  const others = v.crew.filter((m) => !producerIds.has(m.id) && m.id !== v.coordinator?.id && m.id !== v.me.id);
  return (
    <div class="screen prep">
      <p>
        <a class="back-link" href={back} onClick={(e) => (e.preventDefault(), navigate(back))}>
          ← {t.back}
        </a>
      </p>
      <header class="phead">
        <p class="pj-eyebrow">{[v.client, t.title].filter(Boolean).join(' · ')}</p>
        <h1 class="h1">{v.project.name}</h1>
      </header>
      {s.status === 'cancelled' ? (
        <Callout tone="warn" title={t.cancelled}>
          {s.cancelReason}
        </Callout>
      ) : null}
      <section class="panel prep-card" aria-labelledby="prep-when">
        <h2 id="prep-when" class="prep-when">
          {day(s.date, lang)} · {s.start}–{s.end}
        </h2>
        {s.location ? <p class="prep-where">{s.location}</p> : null}
        <div class="prep-actions">
          {s.location ? (
            <a class="btn btn-primary" href={mapsHref(s.location)} target="_blank" rel="noopener noreferrer">
              <Icon name="map-pin" />
              {t.maps}
            </a>
          ) : null}
          <a class="btn" href={calendarHref(s, [v.client, v.project.name].filter(Boolean).join(' – '))} target="_blank" rel="noopener noreferrer">
            <Icon name="calendar" />
            {t.calendar}
          </a>
        </div>
        <ul class="prep-people">
          {v.onSite ? <Person name={v.onSite.name} sub={[v.onSite.title, t.onSite].filter(Boolean).join(' · ')} phone={v.onSite.phone} /> : null}
          {v.producers.map((x) => (
            <Person key={x.id} name={x.name} sub={t.producer} phone={x.phone} />
          ))}
          {v.coordinator && !producerIds.has(v.coordinator.id) ? <Person name={v.coordinator.name} sub={t.coordinator} phone={v.coordinator.phone} /> : null}
          {others.map((m) => (
            <Person key={m.id} name={m.name} sub={t.role[m.role] ?? m.role} role={m.role} phone={m.phone} />
          ))}
        </ul>
      </section>
      <section class="panel prep-card" aria-labelledby="prep-film">
        <h2 id="prep-film" class="blk-title">
          {t.filming}
        </h2>
        {v.videos.length ? (
          <ul class="prep-list">
            {v.videos.map((x) => (
              <li key={x.id}>{x.name}</li>
            ))}
          </ul>
        ) : (
          <p class="muted">{t.wholeProject}</p>
        )}
        {s.plan ? (
          <p class="prep-text">
            <strong>{t.plan}:</strong> {s.plan}
          </p>
        ) : null}
      </section>
      {v.brief.length ? (
        <section class="panel prep-card" aria-labelledby="prep-brief">
          <h2 id="prep-brief" class="blk-title">
            {t.brief}
          </h2>
          {v.brief.map((b) => (
            <p key={b.key} class="prep-text">
              <strong>{b.label}:</strong> {b.text}
            </p>
          ))}
          <div class="prep-actions">
            <a class="btn btn-sm" href={`/projects/${p.projectId}/brief`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${p.projectId}/brief`))}>
              {t.fullBrief}
            </a>
            <a class="btn btn-sm" href={`/projects/${p.projectId}/files`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${p.projectId}/files`))}>
              {t.files}
            </a>
          </div>
        </section>
      ) : null}
      {v.actors.length ? (
        <section class="panel prep-card" aria-labelledby="prep-actors">
          <h2 id="prep-actors" class="blk-title">
            {t.actors}
          </h2>
          <ul class="prep-people">
            {v.actors.map((a) => (
              <li key={a.name} class="prep-person">
                <Avatar name={a.name} size="sm" decorative />
                <span class="prep-who">
                  <strong>{a.name}</strong>
                  {a.role ? <span class="small muted">{a.role}</span> : null}
                </span>
                <span class={`prep-release prep-release--${a.release}`}>
                  {a.release === 'signed' ? <Icon name="check" /> : <Icon name="alert" />}
                  {t[a.release]}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section class="panel prep-card" aria-labelledby="prep-tips">
        <h2 id="prep-tips" class="blk-title">
          {t.tips}
        </h2>
        {v.tips.length ? (
          v.tips.map((x) => (
            <blockquote key={x.date + x.text} class="prep-tip">
              <p>{x.text}</p>
              <footer class="small muted">{t.tipFrom(day(x.date, lang), x.project, x.by)}</footer>
            </blockquote>
          ))
        ) : (
          <p class="small muted">{t.noTips}</p>
        )}
        <p class="small muted">{t.rushes}</p>
      </section>
      {v.readers.length ? (
        <section class="panel prep-card" aria-labelledby="prep-read">
          <h2 id="prep-read" class="blk-title">
            {t.readers}
          </h2>
          <ul class="prep-people">
            {v.readers.map((r) => (
              <li key={r.id} class="prep-person">
                <Avatar name={r.name} size="sm" decorative />
                <span class="prep-who">
                  <strong>{r.name}</strong>
                </span>
                {r.readAt ? (
                  <span class="prep-release prep-release--signed">
                    <Icon name="check" />
                    {stamp(r.readAt, lang)}
                  </span>
                ) : (
                  <span class="small muted">{t.notYet}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {v.me.reader && s.status === 'booked' ? (
        <div class="prep-foot">
          {v.me.readAt ? (
            <p class="prep-done" role="status">
              <Icon name="check" /> {t.readAt(stamp(v.me.readAt, lang))}
            </p>
          ) : (
            <button type="button" class="btn btn-primary prep-read" disabled={busy} onClick={() => void read()}>
              <Icon name="check" />
              {t.readIn}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** The day before a shoot, at the top of My work for the camera crew until they have read in. */
export function PrepBanners(p: { api: Api }) {
  const lang = useLang();
  const t = T[lang];
  const [v, setV] = useState<PrepBanner[]>([]);
  useEffect(() => {
    p.api.get<{ prep: PrepBanner[] }>('/api/shoot-prep').then(
      (r) => setV(r.prep),
      () => setV([])
    );
  }, []);
  if (!v.length) return null;
  return (
    <div class="rush-stack">
      {v.map((b) => {
        const href = `/projects/${b.project.id}/shoots/${b.shootId}`;
        return (
          <section key={b.shootId} class="rush" aria-label={t.banner(`${b.today ? t.today : t.tomorrow} ${b.start}`, b.client)}>
            <span class="rush-ico role-ico--camera">
              <Icon name="video" />
            </span>
            <div class="rush-body">
              <strong>{t.banner(`${b.today ? t.today : t.tomorrow} ${b.start}`, b.client)}</strong>
              <p>{[b.project.name, b.location].filter(Boolean).join(' · ')}</p>
              <p>{t.bannerSub}</p>
              <div class="rush-actions">
                <a class="btn btn-primary" href={href} onClick={(e) => (e.preventDefault(), navigate(href))}>
                  {t.open}
                </a>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
